import React, { useState } from 'react';
import NucleusPage from '../../components/nucleus/NucleusPage';
import { usePlace } from '../../nucleus/place';
import { readPageLines, setPageLines } from '../../nucleus/pageLines';
import Segmented from '../../components/nucleus/Segmented';

// APPEARANCE — how every ofSelf app looks on this device, set right here: one
// sheet, a row per setting, its name and what it changes at the left and its
// choices at the right. Each applies the moment it is pressed.
//
// These are the shell's own preferences, shared by every app. Each is written
// the way the kit writes it (its prefs.js): to this browser, and on an ofself
// domain to a cookie every app on that domain reads, then stamped on the
// document so the change shows at once. The kit does not export its pickers,
// so the same keys are written here.
const APEXES = ['ofself.com', 'ofself.ai'];
const apex = () => {
    try { const h = window.location.hostname; return APEXES.find((a) => h === a || h.endsWith(`.${a}`)) || null; } catch { return null; }
};
function readPref(key) {
    try { const m = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`)); if (m) return decodeURIComponent(m[1]); } catch { /* no cookies */ }
    try { return localStorage.getItem(key); } catch { return null; }
}
function writePref(key, value) {
    try { localStorage.setItem(key, value); } catch { /* private mode */ }
    const a = apex();
    if (!a) return;
    try { document.cookie = `${key}=${encodeURIComponent(value)}; domain=.${a}; path=/; max-age=31536000; SameSite=Lax; Secure`; } catch { /* no cookies */ }
}

// one shell setting: its stored key, the attribute the shell's CSS reads, its choices
function useShellChoice(key, attr, fallback, valid) {
    const [value, setValue] = useState(() => {
        const v = readPref(key);
        if (v != null && valid.includes(v)) return v;
        const a = document.documentElement.getAttribute(attr);
        return a && valid.includes(a) ? a : fallback;
    });
    const choose = (v) => {
        setValue(v);
        writePref(key, v);
        document.documentElement.setAttribute(attr, v);
    };
    return [value, choose];
}

// the gap between blocks, in px: the seam width and the gap, as the kit sets them
// (its own dial runs 0 to 24)
const GAP_MAX = 24;
function useGap() {
    const [gap, setGap] = useState(() => {
        const v = parseFloat(readPref('paradigm_block_sep'));
        return Number.isFinite(v) ? v : 2;
    });
    const choose = (n) => {
        setGap(n);
        writePref('paradigm_block_sep', String(n));
        ['--ofself-seam-w', '--ofself-block-gap'].forEach((v) => document.documentElement.style.setProperty(v, `${n}px`));
    };
    return [gap, choose];
}


function Setting({ name, hint, children }) {
    return (
        <div className="self-setting">
            <div className="self-setting__text">
                <span className="self-setting__name">{name}</span>
                <span className="self-setting__hint">{hint}</span>
            </div>
            {children}
        </div>
    );
}

export default function AppearancePage() {
    const { section, page } = usePlace();
    const [skin, setSkin] = useShellChoice('paradigm_rail_skin', 'data-rail-skin', 'moss', ['light', 'moss', 'dark']);
    const [dots, setDots] = useShellChoice('paradigm_desk_dots', 'data-desk-dots', 'on', ['on', 'off']);
    const [cells, setCells] = useShellChoice('paradigm_cell_view', 'data-cell-view', 'tessellation', ['tessellation', 'linear']);
    const [gap, setGap] = useGap();
    const [lines, setLines] = useState(readPageLines);
    const chooseLines = (v) => { setLines(v); setPageLines(v); };

    return (
        <NucleusPage section={section.label} title={page.label}>
            <div className="self-appearance">
                <div className="self-appearance__sheet">
                    <Setting name="Theme" hint="Light or dark, everywhere.">
                        <Segmented label="Theme" value={skin === 'dark' ? 'dark' : 'moss'} onChange={setSkin}
                            options={[{ value: 'moss', label: 'Light' }, { value: 'dark', label: 'Dark' }]} />
                    </Setting>
                    <Setting name="Dot grid" hint="Dots behind the page, or a plain page.">
                        <Segmented label="Dot grid" value={dots} onChange={setDots}
                            options={[{ value: 'on', label: 'Dots' }, { value: 'off', label: 'Plain' }]} />
                    </Setting>
                    <Setting name="Space between blocks" hint="The gap between the rail, the column and the page.">
                        <label className="self-dial">
                            <input type="range" min="0" max={GAP_MAX} step="1" value={gap} aria-label="Space between blocks, in pixels"
                                style={{ '--fill': `${(gap / GAP_MAX) * 100}%` }} onChange={(e) => setGap(Number(e.target.value))} />
                            <span className="self-dial__value">{gap ? `${gap}px` : 'None'}</span>
                        </label>
                    </Setting>
                    <Setting name="Section column" hint="A section’s pages as equal tiles, or a compact list.">
                        <Segmented label="Section column" value={cells} onChange={setCells}
                            options={[{ value: 'tessellation', label: 'Tiles' }, { value: 'linear', label: 'List' }]} />
                    </Setting>
                    <Setting name="Page descriptions" hint="A line under each page’s name in the section column, saying what it is for.">
                        <Segmented label="Page descriptions" value={lines} onChange={chooseLines}
                            options={[{ value: 'on', label: 'Show' }, { value: 'off', label: 'Hide' }]} />
                    </Setting>
                </div>
                <p className="self-appearance__note">These apply to every ofSelf app on this device, except page descriptions, which are Nucleus’s own.</p>
            </div>
        </NucleusPage>
    );
}
